import { useEffect, useState, useRef } from "react";
import {
    Plus,
    X,
    Edit3,
    Trash2,
    Search,
    ChevronLeft,
    ChevronRight,
    Package,
    Loader2,
    Download,
    Upload,
    FileSpreadsheet,
} from "lucide-react";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

// ─────────────────────────────────────────────────────────────
// SKELETON ROW
// ─────────────────────────────────────────────────────────────
function SkeletonRow() {
    return (
        <tr className="border-t border-gray-100 animate-pulse">
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24" /></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-48" /></td>
            <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded-full w-16" /></td>
            <td className="px-6 py-4">
                <div className="flex justify-center gap-2">
                    <div className="h-8 w-8 bg-gray-200 rounded-lg" />
                    <div className="h-8 w-8 bg-gray-200 rounded-lg" />
                </div>
            </td>
        </tr>
    );
}

// ─────────────────────────────────────────────────────────────
// INPUT COMPONENT
// ─────────────────────────────────────────────────────────────
function Input({ label, value, onChange, placeholder, required = false }) {
    return (
        <div>
            {label && (
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    {label}{required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
            )}
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
            />
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// MODAL COMPONENT
// ─────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative">
                <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>
                <div className="px-6 py-5">{children}</div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
export default function ScrapCodesPage({ setCurrentPage }) {
    const [scraps, setScraps] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);

    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selected, setSelected] = useState(null);

    const [itemNo, setItemNo] = useState("");
    const [description, setDescription] = useState("");
    const [isActive, setIsActive] = useState(true);

    const [search, setSearch] = useState("");

    const [page, setPage] = useState(1);
    const perPage = 8;
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const paginated = filtered.slice((page - 1) * perPage, page * perPage);

    const importRef = useRef(null);

    // ─────────────────────────────
    // FETCH
    // ─────────────────────────────
    const fetchScraps = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/ScrapCodes`);
            if (!res.ok) throw new Error("Failed to fetch scrap codes");
            const data = await res.json();
            setScraps(data);
            setFiltered(data);
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────
    // SAVE (CREATE or UPDATE)
    // ─────────────────────────────
    const saveScrap = async () => {
        if (!itemNo.trim()) {
            Swal.fire("Oops!", "Item No is required.", "warning");
            return;
        }

        const body = {
            Item_No: itemNo.trim(),
            Item_Description: description.trim(),
            IsActive: isActive,
        };

        setSaving(true);
        try {
            const duplicate = scraps.find(
                (s) =>
                    s.item_No?.toLowerCase() === itemNo.trim().toLowerCase() &&
                    (!isEdit || s.id !== selected?.id)
            );

            let url;
            let method;

            if (duplicate) {
                url = `${API_BASE_URL}api/ScrapCodes/${duplicate.id}`;
                method = "PUT";
            } else if (isEdit) {
                url = `${API_BASE_URL}api/ScrapCodes/${selected.id}`;
                method = "PUT";
            } else {
                url = `${API_BASE_URL}api/ScrapCodes`;
                method = "POST";
            }

            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!res.ok) throw new Error("Save failed");

            closeModal();
            await fetchScraps();

            Swal.fire(
                "Success!",
                duplicate
                    ? `"${itemNo.trim()}" already exists — existing record has been updated.`
                    : isEdit
                    ? "Scrap code updated successfully."
                    : "Scrap code created successfully.",
                "success"
            );
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    // ─────────────────────────────
    // DELETE
    // ─────────────────────────────
    const deleteScrap = async (item) => {
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: `Delete "${item.item_No}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, delete it!",
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await fetch(`${API_BASE_URL}api/ScrapCodes/${item.id}`, {
                method: "DELETE",
            });
            if (!res.ok) throw new Error("Delete failed");

            const newTotal = scraps.length - 1;
            const newTotalPages = Math.max(1, Math.ceil(newTotal / perPage));
            if (page > newTotalPages) setPage(newTotalPages);

            await fetchScraps();
            Swal.fire("Deleted!", "Scrap code has been removed.", "success");
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    // ─────────────────────────────
    // EXPORT CSV
    // ─────────────────────────────
    const exportCSV = () => {
        if (scraps.length === 0) {
            Swal.fire("Nothing to export", "No scrap codes available.", "info");
            return;
        }

        const headers = ["Item No", "Description", "Status"];
        const rows = scraps.map((s) => [
            `"${(s.item_No ?? "").replace(/"/g, '""')}"`,
            `"${(s.item_Description ?? "").replace(/"/g, '""')}"`,
            s.isActive ? "Active" : "Inactive",
        ]);

        const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `scrap_codes_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─────────────────────────────
    // EXPORT EXCEL (HTML table trick — opens in Excel)
    // ─────────────────────────────
    const exportExcel = () => {
        if (scraps.length === 0) {
            Swal.fire("Nothing to export", "No scrap codes available.", "info");
            return;
        }

        let table = `<table>
            <thead><tr>
                <th>Item No</th>
                <th>Description</th>
                <th>Status</th>
            </tr></thead>
            <tbody>
                ${scraps
                    .map(
                        (s) => `<tr>
                    <td>${s.item_No ?? ""}</td>
                    <td>${s.item_Description ?? ""}</td>
                    <td>${s.isActive ? "Active" : "Inactive"}</td>
                </tr>`
                    )
                    .join("")}
            </tbody>
        </table>`;

        const blob = new Blob(
            [`<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body>${table}</body></html>`],
            { type: "application/vnd.ms-excel;charset=utf-8;" }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `scrap_codes_${new Date().toISOString().slice(0, 10)}.xls`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─────────────────────────────
    // EXPORT DROPDOWN STATE
    // ─────────────────────────────
    const [showExportMenu, setShowExportMenu] = useState(false);

    // ─────────────────────────────
    // IMPORT CSV/EXCEL
    // ─────────────────────────────
    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!importRef.current) return;
        importRef.current.value = "";

        if (!file) return;

        const ext = file.name.split(".").pop().toLowerCase();
        if (!["csv", "xls", "xlsx"].includes(ext)) {
            Swal.fire("Invalid file", "Please upload a CSV or Excel (.xls/.xlsx) file.", "warning");
            return;
        }

        // Only CSV is parsed client-side; Excel requires a library.
        // For XLS/XLSX we show a friendly message to use CSV.
        if (ext !== "csv") {
            Swal.fire(
                "Use CSV format",
                "Please export your Excel file as CSV first, then import it here. CSV format is required for importing.",
                "info"
            );
            return;
        }

        const text = await file.text();
        const lines = text.trim().split("\n");
        if (lines.length < 2) {
            Swal.fire("Empty file", "The CSV file has no data rows.", "warning");
            return;
        }

        // Skip header row, parse data
        const dataRows = lines.slice(1);
        const parsed = [];

        for (const line of dataRows) {
            // Simple CSV parse (handles quoted fields)
            const cols = line.match(/(".*?"|[^,]+)(?=,|$)/g) ?? [];
            const clean = (v) => (v ?? "").replace(/^"|"$/g, "").replace(/""/g, '"').trim();

            const itemNoVal = clean(cols[0]);
            const descVal = clean(cols[1]);
            const statusVal = clean(cols[2]);

            if (!itemNoVal) continue;

            parsed.push({
                Item_No: itemNoVal,
                Item_Description: descVal,
                IsActive: statusVal.toLowerCase() !== "inactive",
            });
        }

        if (parsed.length === 0) {
            Swal.fire("No valid rows", "Could not find any valid data rows in the file.", "warning");
            return;
        }

        const confirm = await Swal.fire({
            title: `Import ${parsed.length} row(s)?`,
            text: "Existing records with the same Item No will be updated.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, import!",
        });

        if (!confirm.isConfirmed) return;

        setImporting(true);
        let success = 0;
        let failed = 0;

        for (const row of parsed) {
            try {
                const duplicate = scraps.find(
                    (s) => s.item_No?.toLowerCase() === row.Item_No.toLowerCase()
                );

                const url = duplicate
                    ? `${API_BASE_URL}api/ScrapCodes/${duplicate.id}`
                    : `${API_BASE_URL}api/ScrapCodes`;
                const method = duplicate ? "PUT" : "POST";

                const res = await fetch(url, {
                    method,
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(row),
                });

                if (res.ok) success++;
                else failed++;
            } catch {
                failed++;
            }
        }

        setImporting(false);
        await fetchScraps();

        Swal.fire(
            "Import Complete",
            `✅ ${success} imported successfully.${failed > 0 ? `\n❌ ${failed} failed.` : ""}`,
            failed > 0 ? "warning" : "success"
        );
    };

    // ─────────────────────────────
    // SEARCH FILTER
    // ─────────────────────────────
    useEffect(() => {
        const term = search.trim().toLowerCase();
        setFiltered(
            term
                ? scraps.filter(
                      (s) =>
                          s.item_No?.toLowerCase().includes(term) ||
                          s.item_Description?.toLowerCase().includes(term)
                  )
                : scraps
        );
        setPage(1);
    }, [search, scraps]);

    useEffect(() => {
        fetchScraps();
    }, []);

    // Close export menu on outside click
    useEffect(() => {
        const handler = () => setShowExportMenu(false);
        if (showExportMenu) document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, [showExportMenu]);

    // ─────────────────────────────
    // MODAL HELPERS
    // ─────────────────────────────
    const openCreate = () => {
        setIsEdit(false);
        setSelected(null);
        setItemNo("");
        setDescription("");
        setIsActive(true);
        setOpenModal(true);
    };

    const openEdit = (item) => {
        setIsEdit(true);
        setSelected(item);
        setItemNo(item.item_No ?? "");
        setDescription(item.item_Description ?? "");
        setIsActive(item.isActive ?? true);
        setOpenModal(true);
    };

    const closeModal = () => {
        setOpenModal(false);
        setSelected(null);
        setItemNo("");
        setDescription("");
        setIsActive(true);
    };

    // ─────────────────────────────
    // RENDER
    // ─────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">

            {/* Hidden file input for import */}
            <input
                ref={importRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleImportFile}
                className="hidden"
            />

            {/* HEADER */}
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                        <Package className="text-blue-600" size={36} />
                        Scrap Codes
                    </h1>
                    <p className="text-gray-500 mt-1 text-sm">
                        Manage your scrap item codes and descriptions
                    </p>
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex items-center gap-2 flex-wrap">

                    {/* IMPORT */}
                    <button
                        onClick={() => importRef.current?.click()}
                        disabled={importing}
                        title="Import CSV"
                        className="flex items-center gap-2 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-blue-400 text-gray-700 px-4 py-2.5 rounded-xl font-semibold text-sm shadow-sm hover:shadow-md transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {importing ? (
                            <Loader2 size={16} className="animate-spin text-blue-500" />
                        ) : (
                            <Upload size={16} className="text-blue-500" />
                        )}
                        {importing ? "Importing..." : "Import"}
                    </button>

                    {/* EXPORT DROPDOWN */}
                    <div className="relative">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowExportMenu((v) => !v);
                            }}
                            title="Export"
                            className="flex items-center gap-2 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-green-400 text-gray-700 px-4 py-2.5 rounded-xl font-semibold text-sm shadow-sm hover:shadow-md transition-all active:scale-95"
                        >
                            <Download size={16} className="text-green-600" />
                            Export
                        </button>

                        {showExportMenu && (
                            <div
                                className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-xl z-40 overflow-hidden"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    onClick={() => { exportCSV(); setShowExportMenu(false); }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                                >
                                    <FileSpreadsheet size={15} className="text-green-500" />
                                    Export as CSV
                                </button>
                                <div className="border-t border-gray-100" />
                                <button
                                    onClick={() => { exportExcel(); setShowExportMenu(false); }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                                >
                                    <FileSpreadsheet size={15} className="text-emerald-600" />
                                    Export as Excel
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ADD */}
                    <button
                        onClick={openCreate}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-md hover:shadow-lg transition-all active:scale-95"
                    >
                        <Plus size={18} />
                        Add Scrap Code
                    </button>
                </div>
            </div>

            {/* SEARCH */}
            <div className="mb-6 relative max-w-md">
                <Search
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                    type="text"
                    placeholder="Search by item no or description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-11 pr-10 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm shadow-sm transition-all"
                />
                {search && (
                    <button
                        onClick={() => setSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                    >
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm">
                                <th className="px-6 py-4 text-left font-semibold">Item No</th>
                                <th className="px-6 py-4 text-left font-semibold">Description</th>
                                <th className="px-6 py-4 text-left font-semibold">Status</th>
                                <th className="px-6 py-4 text-center font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                Array.from({ length: perPage }).map((_, i) => (
                                    <SkeletonRow key={i} />
                                ))
                            ) : paginated.length > 0 ? (
                                paginated.map((s) => (
                                    <tr key={s.id} className="hover:bg-blue-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                                {s.item_No}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700">
                                            {s.item_Description || (
                                                <span className="text-gray-400 italic">No description</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                                                    s.isActive
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-600"
                                                }`}
                                            >
                                                <span
                                                    className={`w-1.5 h-1.5 rounded-full ${
                                                        s.isActive ? "bg-green-500" : "bg-red-400"
                                                    }`}
                                                />
                                                {s.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openEdit(s)}
                                                    title="Edit"
                                                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md active:scale-95"
                                                >
                                                    <Edit3 size={14} />
                                                </button>
                                                <button
                                                    onClick={() => deleteScrap(s)}
                                                    title="Delete"
                                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md active:scale-95"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-400">
                                            <Package size={48} strokeWidth={1.2} />
                                            <p className="text-base font-medium">No scrap codes found</p>
                                            <p className="text-sm">
                                                {search
                                                    ? "Try a different search term"
                                                    : "Click 'Add Scrap Code' to get started"}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {!loading && filtered.length > 0 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                            Showing{" "}
                            <span className="font-semibold text-gray-700">
                                {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)}
                            </span>{" "}
                            of{" "}
                            <span className="font-semibold text-gray-700">{filtered.length}</span>{" "}
                            results
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={page <= 1}
                                onClick={() => setPage((p) => p - 1)}
                                className="p-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-sm font-semibold text-gray-700 min-w-[80px] text-center">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => p + 1)}
                                className="p-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL */}
            {openModal && (
                <Modal
                    title={isEdit ? "Edit Scrap Code" : "New Scrap Code"}
                    onClose={closeModal}
                >
                    <div className="space-y-4">
                        <Input
                            label="Item No"
                            value={itemNo}
                            onChange={setItemNo}
                            placeholder="e.g. SC-001"
                            required
                        />
                        <Input
                            label="Description"
                            value={description}
                            onChange={setDescription}
                            placeholder="e.g. Defective housing unit"
                        />
                        <label className="flex items-center gap-3 cursor-pointer select-none">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 transition-colors" />
                                <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                                {isActive ? "Active" : "Inactive"}
                            </span>
                        </label>
                    </div>

                    <button
                        onClick={saveScrap}
                        disabled={saving}
                        className="mt-6 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
                    >
                        {saving ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Saving...
                            </>
                        ) : isEdit ? (
                            "Update Scrap Code"
                        ) : (
                            "Create Scrap Code"
                        )}
                    </button>
                </Modal>
            )}
        </div>
    );
}